import { useState } from "react";

import {
  CardActions,
  CardAvatar,
  CardBadge,
  CardBody,
  CardHead,
  CardImage,
  ConversationCard,
  ExpandPill,
  LockClock,
  LockGlyphSlot,
  LockRoot,
  LockShortcut,
  NotificationStack,
  Smartspace,
} from "./android.styles";
import {
  CameraGlyph,
  ChatBadgeGlyph,
  ChevronDownGlyph,
  HomeGlyph,
  LockGlyph,
  PersonGlyph,
  SunGlyph,
} from "./androidGlyphs";
import type { PhoneMessage } from "../phoneMessage";
import { PreviewErrorBubble } from "../PreviewErrorBubble";

function lockScreenBody(message: PhoneMessage, expanded: boolean): string {
  if (message.text) return message.text;
  if (message.imageUrl) return expanded ? "" : "📷 Photo";
  if (message.documentName) return "👤 Contact";
  return "";
}

interface AndroidLockScreenProps {
  message: PhoneMessage;
  onRetry?: () => void;
}

export function AndroidLockScreen({
  message,
  onRetry,
}: AndroidLockScreenProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <LockRoot>
      <LockClock>9:41</LockClock>
      <Smartspace>
        <span>Tue, Sep 8</span>
        <span>·</span>
        <SunGlyph />
        <span>72°</span>
      </Smartspace>

      <NotificationStack>
        {message.isError ? (
          <PreviewErrorBubble
            onRetry={onRetry}
            maxWidth={380}
            borderRadius={28}
            fontSize={14}
            lineHeight={20}
          />
        ) : (
          <ConversationCard
            type="button"
            aria-expanded={expanded}
            aria-label="Messages notification"
            onClick={() => setExpanded((value) => !value)}
          >
            <CardAvatar>
              <PersonGlyph size={26} />
              <CardBadge>
                <ChatBadgeGlyph />
              </CardBadge>
            </CardAvatar>
            <CardHead>
              <b>{message.sender}</b>
              <span>{expanded ? "· Messages · now" : "· now"}</span>
            </CardHead>
            <CardBody data-testid="android-lock-body" expanded={expanded}>
              {lockScreenBody(message, expanded)}
            </CardBody>
            {expanded && message.imageUrl ? (
              <CardImage>
                <img src={message.imageUrl} alt={message.imageAlt} />
              </CardImage>
            ) : null}
            {expanded ? (
              <CardActions>
                <span>Mark as read</span>
                <span>Reply</span>
              </CardActions>
            ) : null}
            <ExpandPill expanded={expanded}>
              <ChevronDownGlyph />
            </ExpandPill>
          </ConversationCard>
        )}
      </NotificationStack>

      <LockShortcut side="left">
        <HomeGlyph />
      </LockShortcut>
      <LockShortcut side="right">
        <CameraGlyph />
      </LockShortcut>
      <LockGlyphSlot>
        <LockGlyph />
      </LockGlyphSlot>
    </LockRoot>
  );
}
